import * as React from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '../../utils';

export interface DisclosureProps extends Omit<
  React.DetailsHTMLAttributes<HTMLDetailsElement>,
  'title' | 'open' | 'onToggle'
> {
  /** Always visible, in the clickable summary row. */
  title: React.ReactNode;
  /** Shown next to the title while collapsed: what the hidden fields hold. */
  summary?: React.ReactNode;
  /** Controlled state. Omit it (with `defaultOpen`) for an uncontrolled one. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
}

/**
 * A native `<details>`/`<summary>` for "more options" blocks. The content
 * stays in the DOM while collapsed, so form fields registered inside keep
 * their values. The summary click is intercepted: the `open` prop is the
 * only source of truth, the browser never toggles the element on its own.
 */
const Disclosure = React.forwardRef<HTMLDetailsElement, DisclosureProps>(
  (
    {
      title,
      summary,
      open: openProp,
      defaultOpen = false,
      onOpenChange,
      className,
      contentClassName,
      children,
      ...props
    },
    ref,
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const open = openProp ?? uncontrolledOpen;

    const toggle = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      if (openProp === undefined) setUncontrolledOpen(!open);
      onOpenChange?.(!open);
    };

    return (
      <details
        ref={ref}
        open={open}
        className={cn(
          'group/disclosure rounded-lg border border-dashed border-border p-3',
          className,
        )}
        {...props}
      >
        <summary
          onClick={toggle}
          className="flex cursor-pointer list-none items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground [&::-webkit-details-marker]:hidden"
        >
          <ChevronRight
            aria-hidden
            className="h-3.5 w-3.5 shrink-0 transition-transform group-open/disclosure:rotate-90"
          />
          <span className="shrink-0">{title}</span>
          {!open && summary && (
            <span
              className="min-w-0 truncate font-normal"
              data-slot="disclosure-summary"
            >
              {summary}
            </span>
          )}
        </summary>
        <div className={cn('mt-3 space-y-4', contentClassName)}>{children}</div>
      </details>
    );
  },
);
Disclosure.displayName = 'Disclosure';

export { Disclosure };
